/**
 * Utility functions for detecting if the website is running in the ArchiPro native app
 */

/**
 * Check if the current environment is the ArchiPro native app
 * @returns boolean indicating if running in ArchiPro native app
 */
// Re-export React for the hook
import React from 'react';

export function isAPNativeApp(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    // Check for the global ArchiPro native app detection variable
    return !!(
        window as Window & {
            __ARCHIPRO_MOBILE_APP__?: { isAPNativeApp?: boolean };
        }
    ).__ARCHIPRO_MOBILE_APP__?.isAPNativeApp;
}

/**
 * Get ArchiPro native app information if available
 * @returns ArchiPro native app info object or null
 */
export function getAPNativeAppInfo(): {
    isAPNativeApp: boolean;
    userAgent: string;
    timestamp: number;
} | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const nativeAppData = (
        window as Window & {
            __ARCHIPRO_MOBILE_APP__?: {
                isAPNativeApp?: boolean;
                userAgent?: string;
                timestamp?: number;
            };
        }
    ).__ARCHIPRO_MOBILE_APP__;
    if (!nativeAppData?.isAPNativeApp) {
        return null;
    }

    return {
        isAPNativeApp: nativeAppData.isAPNativeApp,
        userAgent: nativeAppData.userAgent || navigator.userAgent,
        timestamp: nativeAppData.timestamp || Date.now(),
    };
}

/**
 * Check if the document has the ArchiPro native app CSS class
 * This is a fallback method in case the global variable isn't available
 * @returns boolean indicating if ArchiPro native app class is present
 */
export function hasAPNativeAppClass(): boolean {
    if (typeof document === 'undefined') {
        return false;
    }

    const rootElements = [
        document.documentElement,
        document.body,
        document.getElementById('root'),
        document.getElementById('app'),
        document.getElementById('app-page-layout'),
    ];

    return rootElements.some((element) =>
        element?.classList.contains('ap-mobile-app')
    );
}

/**
 * Comprehensive check for ArchiPro native app environment
 * Combines multiple detection methods for reliability
 * @returns boolean indicating if running in ArchiPro native app
 */
export function isRunningInAPNativeApp(): boolean {
    return isAPNativeApp() || hasAPNativeAppClass();
}

/**
 * Hook for React components to detect ArchiPro native app
 * @returns object with ArchiPro native app detection state and info
 */
export function useAPNativeAppDetection() {
    const [isAPNative, setIsAPNative] = React.useState(false);
    const [nativeAppInfo, setNativeAppInfo] =
        React.useState<ReturnType<typeof getAPNativeAppInfo>>(null);
    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
        const checkAPNativeApp = () => {
            const isAPNativeApp = isRunningInAPNativeApp();
            const info = getAPNativeAppInfo();

            setIsAPNative(isAPNativeApp);
            setNativeAppInfo(info);
            setIsLoading(false);
        };

        // Check immediately
        checkAPNativeApp();

        // Check again after a short delay to catch late-loading scripts
        const timeoutId = setTimeout(checkAPNativeApp, 100);

        // Listen for custom events that might indicate ArchiPro native app detection
        const handleAPNativeAppDetected = () => {
            checkAPNativeApp();
        };

        // Listen for DOM changes that might indicate ArchiPro native app class addition
        const observer = new MutationObserver(() => {
            checkAPNativeApp();
        });

        if (document.body) {
            observer.observe(document.body, {
                childList: true,
                attributes: true,
                subtree: true,
                attributeFilter: ['class'],
            });
        }

        window.addEventListener(
            'ap-native-app-detected',
            handleAPNativeAppDetected
        );

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener(
                'ap-native-app-detected',
                handleAPNativeAppDetected
            );
            observer.disconnect();
        };
    }, []);

    return {
        isAPNativeApp: isAPNative,
        nativeAppInfo,
        hasAPNativeAppClass: hasAPNativeAppClass(),
        isLoading,
        // Utility functions for convenience
        isNotAPNativeApp: !isAPNative,
    };
}
